import { createElement, setText, getUiRoot, removeElement } from './dom';
import { setLabel } from './i18n';
import { createIcon } from './icons';
import { toHudModel } from './hudModel';
import type { Hud, HudState, HudIcon } from './types';

function showCoords(label: HTMLElement, text: string | null): void {
  label.hidden = text === null;
  if (text !== null && label.textContent !== text) setText(label, text);
}

function iconUpdater(container: HTMLElement): (name: HudIcon) => void {
  let currentIconName: HudIcon | null = null;
  let iconElement: SVGElement | null = null;
  return (name: HudIcon): void => {
    if (name === currentIconName) return;

    if (iconElement) {
      container.removeChild(iconElement);
    }

    iconElement = createIcon(name, 20);
    container.appendChild(iconElement);
    currentIconName = name;
  };
}

function menuReadout(menu: HTMLElement, cycleQuality: () => void) {
  const quality = createElement('button', 'graphics-quality', menu);
  quality.type = 'button';
  setText(createElement('span', '', quality), 'Graphics');
  const tierLabel = createElement('span', 'hud-tier', quality);
  quality.addEventListener('click', cycleQuality);
  const coordsLabel = createElement('div', 'hud-coords', menu);

  return { quality, tierLabel, coordsLabel };
}

export function createHud(menu: HTMLElement, cycleQuality: () => void): Hud {
  const container = createElement('div', 'hud', getUiRoot());
  const row = createElement('div', 'hud-row', container);

  const iconContainer = createElement('span', 'hud-icon', row);
  const updateIcon = iconUpdater(iconContainer);

  const modeLabel = createElement('span', 'hud-mode sr-only', row);
  const speedLabel = createElement('span', 'hud-speed sr-only', row);

  const { quality, tierLabel, coordsLabel } = menuReadout(menu, cycleQuality);
  const connectionLabel = createElement('div', 'city-connection sr-only', container);
  connectionLabel.setAttribute('aria-live', 'polite');
  const hintLabel = createElement('div', 'hud-hint sr-only', container);

  function update(state: HudState): void {
    const model = toHudModel(state);

    updateIcon(model.icon);
    setLabel(iconContainer, 'aria-label', model.modeLabel);
    iconContainer.setAttribute('role', 'img');
    setText(modeLabel, model.modeLabel);

    if (model.speedText) {
      setText(speedLabel, model.speedText);
      speedLabel.style.display = '';
    } else {
      speedLabel.style.display = 'none';
    }

    setText(tierLabel, model.tierText);
    setLabel(quality, 'aria-label', `Graphics: ${model.tierText}`);
    setText(hintLabel, model.hint);
    showCoords(coordsLabel, model.coordsText);
  }

  function dispose(): void {
    removeElement(container);
    removeElement(quality);
    removeElement(coordsLabel);
  }

  return {
    update,
    dispose,
    setConnection(value: string): void {
      setText(connectionLabel, value);
    },
  };
}
